import React from 'react';
import { Box, Card, Link as MuiLink, Typography } from '@mui/material';
import {
  ErrorOutline, WarningAmber, InfoOutlined, ChevronRight,
} from '@mui/icons-material';
import type { AttentionItem, AttentionSeverity } from '../../services/dashboard-service';
import { NavigateFn, shortDate } from './shared';

const TONE: Record<AttentionSeverity, { icon: React.ReactNode; color: string; label: string }> = {
  error: { icon: <ErrorOutline fontSize="small" />, color: 'var(--danger-text)', label: 'Urgent' },
  warning: { icon: <WarningAmber fontSize="small" />, color: 'var(--warn-text)', label: 'Attention' },
  info: { icon: <InfoOutlined fontSize="small" />, color: 'var(--info-text)', label: 'To do' },
};

/**
 * Section 1: the short list of things that need a person, each a single row
 * that goes straight to the fix. Renders NOTHING when the list is empty -- a
 * permanent "all clear" panel is a panel people learn to skip, and then miss
 * the day it is not clear.
 */
export const NeedsAttention: React.FC<{ items: AttentionItem[]; onNavigate: NavigateFn }> = ({
  items, onNavigate,
}) => {
  if (!items || items.length === 0) return null;
  return (
    <Card component="section" aria-label="Needs attention" sx={{ mb: 3 }}>
      <Box sx={{ px: 2, pt: 1.5, pb: 0.5, display: 'flex', alignItems: 'baseline', gap: 1 }}>
        <Typography variant="h6" component="h2" sx={{ fontSize: '1.05rem', fontWeight: 650 }}>
          Needs attention
        </Typography>
        <Typography variant="caption" sx={{ color: 'var(--text-secondary)' }}>
          {items.length} {items.length === 1 ? 'item' : 'items'}
        </Typography>
      </Box>
      <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0 }}>
        {items.map((it, i) => {
          const tone = TONE[it.severity] ?? TONE.info;
          return (
            <Box component="li" key={`${it.kind}-${i}`}
              sx={{ borderTop: i === 0 ? 'none' : '1px solid var(--border-soft)' }}>
              <Box
                role="button"
                tabIndex={0}
                aria-label={`${tone.label}: ${it.title}`}
                onClick={() => onNavigate(it.link)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onNavigate(it.link);
                  }
                }}
                sx={{
                  display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1.1,
                  cursor: 'pointer', transition: 'background 0.12s ease',
                  '&:hover': { background: 'var(--glass-1)' },
                  '&:focus-visible': { outline: '2px solid var(--accent)', outlineOffset: '-2px' },
                }}
              >
                <Box sx={{ color: tone.color, display: 'flex', flexShrink: 0 }} aria-hidden>
                  {tone.icon}
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{it.title}</Typography>
                  {it.detail && (
                    <Typography variant="caption" sx={{ color: 'var(--text-secondary)', display: 'block' }}>
                      {it.detail}
                    </Typography>
                  )}
                  {it.accounts && it.accounts.length > 0 && (
                    <Typography variant="caption" component="div" sx={{ color: 'var(--text-secondary)' }}>
                      {it.accounts.map((a, j) => (
                        <React.Fragment key={a.id}>
                          {j > 0 && ' · '}
                          <MuiLink
                            component="button"
                            type="button"
                            underline="hover"
                            onClick={(e: React.MouseEvent) => {
                              // The row also navigates; the account link is
                              // the more specific target.
                              e.stopPropagation();
                              onNavigate({ to: '/accounts/$id', params: { id: a.id } });
                            }}
                            sx={{ fontSize: 'inherit', color: 'var(--text)', verticalAlign: 'baseline' }}
                          >
                            {a.name}
                          </MuiLink>
                          {a.last_activity_on && ` (${shortDate(a.last_activity_on)})`}
                        </React.Fragment>
                      ))}
                      {it.more ? ` and ${it.more} more` : ''}
                    </Typography>
                  )}
                </Box>
                <ChevronRight fontSize="small" sx={{ color: 'var(--text-faint)', flexShrink: 0 }} />
              </Box>
            </Box>
          );
        })}
      </Box>
    </Card>
  );
};
